import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'

export interface LinkedIngredient {
  id: number
  productName: string
  productNameNumber: string
}

export interface ProductWithIngredients {
  posPlu: number
  productName: string
  linkedIngredients: LinkedIngredient[]
}

interface AdminToolProductIngredientsState {
  products: ProductWithIngredients[]
  loading: boolean
  meta: {
    total: number
    pages: number
  }
  error: string | null
  stats: {
    totalProducts: number
    productsWithLinkedIngredients: number
    productsWithoutLinkedIngredients: number
  } | null
  statsLoading: boolean
}

interface ProductIngredientsQueryOptions {
  take?: number
  skip?: number
  sortBy?: string
  filters?: Record<string, string>
}

interface ProductIngredientsResponse {
  data: ProductWithIngredients[]
  meta: {
    total: number
    pages: number
  }
}

const initialState: AdminToolProductIngredientsState = {
  products: [],
  loading: false,
  meta: {
    total: 0,
    pages: 0
  },
  error: null,
  stats: null,
  statsLoading: false
}

export const fetchAdminToolProductIngredients = createAsyncThunk<
  ProductIngredientsResponse,
  ProductIngredientsQueryOptions,
  Redux
>('adminToolsProductIngredients/fetch', async (queryOptions, { extra }) => {
  const { data } = await extra.httpClient.get('cerebro-product-companies/products-with-ingredients/list', {
    params: queryOptions
  })

  if (Array.isArray(data?.data)) {
    return {
      data: data.data.map((product: ProductWithIngredients) => ({
        ...product,
        linkedIngredients: Array.isArray(product.linkedIngredients) ? product.linkedIngredients : []
      })),
      meta: data.meta || {
        total: data.data.length,
        pages: 1
      }
    }
  }

  return {
    data: [],
    meta: {
      total: 0,
      pages: 0
    }
  }
})

export const getProductIngredientStats = createAsyncThunk(
  'adminToolsProductIngredients/getStats',
  async (_, { extra }) => {
    const { data: responseData } = await (extra as Redux['extra']).httpClient.get(
      'cerebro-product-companies/products-with-ingredients/stats'
    )

    return responseData?.data ?? responseData
  }
)

const adminToolsProductIngredientsSlice = createSlice({
  name: 'adminToolsProductIngredients',
  initialState,
  reducers: {
    clearAdminToolProductIngredientsError: state => {
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchAdminToolProductIngredients.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchAdminToolProductIngredients.fulfilled, (state, action) => {
      state.loading = false
      state.products = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(fetchAdminToolProductIngredients.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message || 'Failed to fetch products'
    })

    builder.addCase(getProductIngredientStats.pending, state => {
      state.statsLoading = true
      state.error = null
    })
    builder.addCase(getProductIngredientStats.fulfilled, (state, action) => {
      state.statsLoading = false
      state.stats = action.payload
    })
    builder.addCase(getProductIngredientStats.rejected, (state, action) => {
      state.statsLoading = false
      state.error = action.error.message || 'Failed to fetch product stats'
    })
  }
})

export const { clearAdminToolProductIngredientsError } = adminToolsProductIngredientsSlice.actions

export default adminToolsProductIngredientsSlice.reducer
